import { cast, LocalDate, maybe } from "@flockcover/flock-svc-core-types"
import * as t from "io-ts"
import { UUID } from "io-ts-types/lib/UUID"

export const FleetStats = t.type({
  totalVehicles: t.number,
  connectedTotal: t.number,
  connectedPercent: t.number,
  disconnectedTotal: t.number,
  disconnectedPercent: t.number,
  scheduledTotal: t.number,
  scheduledPercent: t.number,
  onCoverTotal: t.number,
  onCoverPercent: t.number,
  offCoverTotal: t.number,
  offCoverPercent: t.number,
})

export const StatusType = t.union([t.literal(`scheduled`), t.literal(`on_cover`), t.literal(`off_cover`), t.undefined])

const EntryInput = t.intersection([
  t.type({
    id: t.string,
  }),
  t.partial({
    search: t.string,
    coverStatus: StatusType,
    include: t.string,
    limit: t.number,
    after: t.string,
  }),
])

const ModernEntryInput = t.intersection([EntryInput, t.type({ modern: t.literal(true) })])

const LegacyEntryInput = t.type({
  policyId: t.string,
  modern: t.literal(false),
})

export type Status = t.TypeOf<typeof StatusType>
export type FleetEntryInput = t.TypeOf<typeof EntryInput>
export type FindFleetModernInput = t.TypeOf<typeof ModernEntryInput>
export type FindFleetLegacyInput = t.TypeOf<typeof LegacyEntryInput>
export type FindFleetInput = FindFleetModernInput

// --------------------------------------------------------------------------------------------------------------------

const baseFragment = {
  registrationNumber: t.string,
  onCoverDate: LocalDate,
  offCoverDate: maybe(LocalDate),
}
const BaseFragmentType = t.type(baseFragment)
export type BaseFragment = t.TypeOf<typeof BaseFragmentType>

const resolvedFragment = {
  vehicleId: UUID,
  make: t.string,
  model: t.string,
  fuelType: maybe(t.string),
  abiCode: maybe(t.string),
  abiGroup: maybe(t.number),
  grossWeight: maybe(t.number),
  nominalStartYear: maybe(t.number),
  numberOfSeats: maybe(t.number),
  trim: maybe(t.string),
  type: t.string,
}
const ResolvedFragmentType = t.type(resolvedFragment)
export type ResolvedFragment = t.TypeOf<typeof ResolvedFragmentType>

export type BaseFleetEntry = BaseFragment
export type AddedFleetEntry = BaseFragment & Partial<ResolvedFragment>
export type ResolvedFleetEntry = BaseFragment & ResolvedFragment

// NOTE: uniqueRef is for use in the frontend, vehicle ids may not be unique
// so we generated a uniqueRef using a combo for VRN, onCoverDate, offCoverDate
// when pulling in the data from the backend.
// This uses createUniqueRef() - src/graphql/queries/vehicles/utils.ts
const UniqueRefType = t.union([t.string, t.undefined])

const FleetEntryType = t.type({
  ...baseFragment,
  ...resolvedFragment,
  // Optional as the legacy entries don't contain an ID.
  id: t.string,
  sentToMID: maybe(LocalDate),
  status: StatusType,
  uniqueRef: UniqueRefType,
  isConnected: t.boolean,
  createdAt: maybe(LocalDate),
})

const FleetType = t.readonlyArray(FleetEntryType)

// --------------------------------------------------------------------------------------------------------------------

export type UniqueRef = t.TypeOf<typeof UniqueRefType>
export type FleetEntry = t.TypeOf<typeof FleetEntryType>
export type Fleet = t.TypeOf<typeof FleetType>
export const makeFleet = cast(FleetType)
export const makeFleetEntries = cast(t.readonlyArray(FleetEntryType))
